import { isShortCode } from '../services/barcode-mapping.service';

/**
 * Tag links: what a CheckPoint NFC tag holds — https://<host>/t/<short code>.
 *
 * A URL (rather than the bare code) lets the phone act on the tag outside the scan
 * page: Android hands it to the installed app (NDEF_DISCOVERED intent filter) or,
 * without the app, opens it in the browser; iOS opens it via universal links. The
 * /t/:code route resolves the code like any scanned code. See docs/features/nfc-tags.md.
 */
export const TAG_LINK_PATH = '/t/';

/** `base` is an origin such as "https://checkpoint.int.gardenbaysoftware.com". */
export function tagLink(base: string, code: string): string {
  return `${base.replace(/\/+$/, '')}${TAG_LINK_PATH}${code}`;
}

/** The short code in a tag link (any host), or null when `text` isn't one. */
export function codeFromTagLink(text: string | null | undefined): string | null {
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const match = url.pathname.match(/^\/t\/([^/]+)\/?$/);
  const code = match?.[1].toUpperCase() ?? null;
  return isShortCode(code) ? code : null;
}

/**
 * Store listings offered on the tag-link landing page when a phone without the app
 * opens a tag link in the browser. Null until the app is published; without a link the
 * landing page is skipped and the browser goes straight to the form.
 */
export const APP_STORE_LINKS: Record<'android' | 'ios', string | null> = {
  android: null,
  ios: null,
};

/** Store link for the mobile browser this runs in, if the app is listed for it. */
export function appStoreLinkForBrowser(userAgent: string): string | null {
  if (/android/i.test(userAgent)) return APP_STORE_LINKS.android;
  if (/iphone|ipad|ipod/i.test(userAgent)) return APP_STORE_LINKS.ios;
  return null;
}
